import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { API_ENDPOINTS } from "../../constants/API_ENDPOINTS";

const Registro = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaId, setCaptchaId] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState("");

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await api.get(API_ENDPOINTS.REGISTER_CHALLENGE);
      setCaptchaId(res.data.captcha_id);
      setCaptchaQuestion(res.data.question);
    } catch (err) {
      console.error("Failed to load captcha", err);
      setCaptchaQuestion("");
      setCaptchaId("");
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const validationSchema = Yup.object({
    username: Yup.string()
      .min(3, t("register.username_min"))
      .required(t("register.username_required")),
    email: Yup.string()
      .email(t("register.email_invalid"))
      .required(t("register.email_required")),
    whatsapp_number: Yup.string(),
    password: Yup.string()
      .min(6, t("register.password_min"))
      .required(t("register.password_required")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("register.passwords_must_match"))
      .required(t("register.confirm_password_required")),
    captcha_answer: Yup.string().required(t("register.captcha_required")),
    website: Yup.string(), // honeypot
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      whatsapp_number: "",
      password: "",
      confirmPassword: "",
      captcha_answer: "",
      website: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setFieldValue }) => {
      setError(null);
      try {
        await register(
          values.username,
          values.email,
          values.password,
          values.confirmPassword,
          values.whatsapp_number || undefined,
          true,
          false,
          {
            captcha_id: captchaId,
            captcha_answer: values.captcha_answer,
          }
        );
        navigate("/");
      } catch (err: any) {
        setError(err.message || t("register.failed"));
        setFieldValue("captcha_answer", "");
        await loadCaptcha();
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h5">
          {t("register.title")}
        </Typography>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label={t("register.username")}
            name="username"
            autoComplete="username"
            autoFocus
            value={formik.values.username}
            onChange={formik.handleChange}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label={t("register.email")}
            name="email"
            autoComplete="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            label={t("register.whatsapp_number")}
            name="whatsapp_number"
            value={formik.values.whatsapp_number}
            onChange={formik.handleChange}
            helperText={t("register.whatsapp_helper")}
            fullWidth
            margin="normal"
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t("register.password")}
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="new-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword
                        ? t("register.hide_password")
                        : t("register.show_password")
                    }
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label={t("register.confirm_password")}
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            autoComplete="new-password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={
              formik.touched.confirmPassword &&
              Boolean(formik.errors.confirmPassword)
            }
            helperText={
              formik.touched.confirmPassword && formik.errors.confirmPassword
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showConfirmPassword
                        ? t("register.hide_password")
                        : t("register.show_password")
                    }
                    onClick={() => setShowConfirmPassword((show) => !show)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Honeypot — hidden from real users */}
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: "-10000px",
              top: "auto",
              width: 1,
              height: 1,
              overflow: "hidden",
            }}
          >
            <TextField
              tabIndex={-1}
              autoComplete="off"
              name="website"
              label="Website"
              value={formik.values.website}
              onChange={formik.handleChange}
            />
          </Box>
          <TextField
            margin="normal"
            required
            fullWidth
            name="captcha_answer"
            label={
              captchaQuestion
                ? t("register.captcha_label", { question: captchaQuestion })
                : t("register.captcha_loading")
            }
            id="captcha_answer"
            autoComplete="off"
            value={formik.values.captcha_answer}
            onChange={formik.handleChange}
            error={
              formik.touched.captcha_answer &&
              Boolean(formik.errors.captcha_answer)
            }
            helperText={
              (formik.touched.captcha_answer &&
                formik.errors.captcha_answer) ||
              t("register.captcha_helper")
            }
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={formik.isSubmitting || !captchaId}
          >
            {formik.isSubmitting
              ? t("register.registering")
              : t("register.register")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Registro;
